import React, { useEffect, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, FlatList } from 'react-native';
import { Icons } from '../../Components';
import Colors from '../../constants/colors';
import { ProfileApi } from '../api';
import { button_styles } from '../../constants/styles';
import type { FocusPositionProps, MyPlayerProps } from '../../types';
import { Button, Text, View, TextInput } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type BasicInfoManagerProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onNext:() => void,
    onClose:() => void,
    onPlayerUpdate:(player:MyPlayerProps) => void 
}

const sections = ['header','username','name']
const BasicInfoManager = ({ player, walkthrough, onFocusPosition, onPlayerUpdate, onNext, onClose }:BasicInfoManagerProps) => {
    const C = useColors();
    const [ draft_player, setDraftPlayer ] = useState<MyPlayerProps>(player)
    const [ loading, setLoading ] = useState(false);
    const [ username_valid, setUsernameValid ] = useState<{
        loading:boolean,
        valid:boolean
    }>({
        loading:false,
        valid:true
    });

    useEffect(() => {
        setDraftPlayer(player)
    },[player])


    const is_changed = JSON.stringify(player) != JSON.stringify(draft_player) ? true : false

    const updatePlayer = async() => {
        if(loading){ return }
        setLoading(true)
        let new_player:MyPlayerProps = { ...draft_player };
        if(`${draft_player.first_name.toLowerCase()}${draft_player.last_name.toLowerCase()}` != `${player.first_name.toLowerCase()}${player.last_name.toLowerCase()}`){
            new_player = await ProfileApi.updatePlayerName(draft_player.first_name, draft_player.last_name)
        }
        if(player.username.toLowerCase() != draft_player.username.toLowerCase()){
            if(!username_valid.valid){ return }
            new_player = await ProfileApi.updatePlayerUsername(draft_player.username)
        }
        onPlayerUpdate(new_player)
        setLoading(false);
        return
    }

    const handleNext = async() => {
        if(!is_changed){ return onNext() }
        await updatePlayer()
        return onNext()
    }

    const allow_next = username_valid.valid && !username_valid.loading && draft_player.first_name && draft_player.last_name ? true : false

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!walkthrough){ return <></> }
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>Let's start with the basics</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>You can change this later, but will be required to verify your account.</Text>
                    </View>
                )
            case 'username':
                return (
                    <View style={{ padding:30 }}>
                    <Text weight='bold' size={18} theme='h1'>USERNAME</Text>
                    <Text style={{ marginTop:5 }} theme='h2'>To start, we randomly generate a username for you!  Change it if you'd like here.</Text>
                    <View type='input' style={{ marginTop:20, flexDirection:'row', alignItems:'center' }}>
                        <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                            <Text theme='h1'>@</Text>
                        </View>
                        <TextInput
                            value={draft_player.username}
                            style={{ flex:1, borderRadius:0 }}
                            
                            onFocusPosition={onFocusPosition}
                            onBlur={async() => {
                                if(draft_player.username.toLowerCase() == player.username.toLowerCase()){ return }//USERNAME HAS NOT CHANGED
                                setUsernameValid({ ...username_valid, loading:true })
                                const is_valid = await ProfileApi.isAttributeValid('username', draft_player.username)
                                setTimeout(() => {
                                    setUsernameValid({ loading: false, valid: is_valid });
                                }, 500);
                            }}
                            onChangeText={(text) => {
                                setDraftPlayer({ ...draft_player, username: text })
                                if(text.toLowerCase() != player.username.toLowerCase()){
                                    setUsernameValid({ loading:false, valid:false })
                                } else {
                                    setUsernameValid({ loading: false, valid:true });
                                }
                            }}
                        />
                        <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                        {username_valid.loading ?
                        <ActivityIndicator size='small' color={Colors.brand.midnight}/>
                        :
                        <View transparent>
                            {username_valid.valid ?
                            <Icons.CheckIcon size={16} color={C.text.success} />
                            :
                            <Icons.AlertIcon size={16} color={C.text.warning} />
                            }
                        </View>
                        }
                        </View>
                    </View>
                </View>
                )
            case 'name':
                return (
                    <View style={{ padding:30 }}>
                        <Text weight='bold' size={18} theme='h1'>NAME</Text>
                        <Text style={{ marginTop:5 }} theme='h2'>Add / Update your name below.  This is required to verify your account</Text>
                        <View type='input' style={{ marginTop:20, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.UserIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={draft_player.first_name}
                                placeholder='First Name'
                                onFocusPosition={onFocusPosition}
                                placeholderTextColor={Colors.brand.slate}
                                editable={player.vouched_status != 'unverified' ? false : true}
                                style={{ flex:1, borderTopLeftRadius:0, borderBottomLeftRadius:0 }}
                                onChangeText={(text) => setDraftPlayer({ ...draft_player, first_name: text })}
                            />
                        </View>
                        <View type='input' style={{ marginTop:10, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.UserIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={draft_player.last_name}
                                placeholder='Last Name'
                                
                                onFocusPosition={onFocusPosition}
                                placeholderTextColor={Colors.brand.slate}
                                editable={player.vouched_status != 'unverified' ? false : true}
                                style={{ flex:1, borderTopLeftRadius:0, borderBottomLeftRadius:0 }}
                                onChangeText={(text) => setDraftPlayer({ ...draft_player, last_name: text })}
                            />
                        </View>
                        {player.vouched_status !== 'unverified' && (
                            <View style={{ marginTop: 15, padding: 15, backgroundColor:'rgba(255, 193, 7, 0.1)', borderRadius: 8, borderWidth: 1, borderColor: C.absolutes.utility.warning }}>
                                <View transparent style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                    <Icons.AlertIcon size={20} color={C.text.warning}/>
                                    <View transparent style={{ flex: 1, marginLeft:10 }}>
                                        <Text theme='h2' size={14} style={{ marginBottom: 8 }}>Name Update Restricted</Text>
                                        <Text theme='description' size={12} style={{ lineHeight: 18 }}>
                                            Your account verification status prevents name updates. Please contact support for assistance with changing your legal name.
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        )}
                    </View> 
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'basic_sections'}
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            {walkthrough ?
            <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                <Button
                    title='EXIT SETUP'
                    type='text'
                    onPress={() => onClose()}
                />
                <TouchableOpacity 
                    disabled={!allow_next}
                    style={allow_next ? button_styles.wizard_valid : button_styles.wizard_invalid}
                    onPress={() => handleNext()}
                    >
                    <Icons.ChevronIcon size={20} direction='right' color={Colors.brand.midnight}/>
                </TouchableOpacity>
            </View>
            :
            <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                <Button
                    style={{ flex:1, marginRight:5 }}
                    title='BACK'
                    type='close'
                    onPress={() => onClose()}
                />
                <Button
                    style={{ flex:3, opacity:allow_next && is_changed?1:0.5 }}
                    disabled={!is_changed || !allow_next}
                    title='UPDATE'
                    type='success'
                    onPress={async() => {
                        await updatePlayer()
                        onClose()
                    }}
                />
            </View>
            }
        </View>
    )
}

export default BasicInfoManager